import React, { useState } from 'react';
import { Button, Card, DatePicker, Form, Input, Select, Steps, Table, Typography, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePayroll } from '../../hooks/usePayroll';
import PayrollStatusBadge from '../../components/PayrollStatusBadge';
import { PayrollEntry } from '../../types';

const { Title, Text } = Typography;
const { Option } = Select;
const STEP_LABELS = ['Create Period', 'Generate Entries', 'Review', 'Process', 'Approve & Post'];

const fmt = (n: number) => new Intl.NumberFormat('en-TZ', { minimumFractionDigits: 2 }).format(n);

const PayrollRunWizardView: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const {
    activePeriod, entries, isLoading,
    createPeriod, generateEntries, fetchEntries, processPayroll, approvePayroll, setActivePeriod,
  } = usePayroll();

  // Step 0: Create period
  const handleCreatePeriod = async () => {
    const values = await form.validateFields();
    const created = await createPeriod({
      ...values,
      startDate: values.dateRange[0].format('YYYY-MM-DD'),
      endDate: values.dateRange[1].format('YYYY-MM-DD'),
      payDate: values.payDate?.format('YYYY-MM-DD'),
    });
    if (created) { setActivePeriod(created); setCurrentStep(1); }
  };

  // Step 1: Generate entries
  const handleGenerate = async () => {
    if (!activePeriod) return;
    await generateEntries(activePeriod.id);
    await fetchEntries(activePeriod.id);
    setCurrentStep(2);
  };

  // Step 2: Review — just advance
  const handleReviewNext = () => setCurrentStep(3);

  // Step 3: Process
  const handleProcess = async () => {
    if (!activePeriod) return;
    await processPayroll(activePeriod.id);
    setCurrentStep(4);
  };

  // Step 4: Approve & Post GL
  const handleApprove = async () => {
    if (!activePeriod) return;
    await approvePayroll(activePeriod.id);
    navigate(`/hr/payroll/${activePeriod.id}`);
  };

  const totalGross = entries.reduce((s, e) => s + e.grossPay, 0);
  const totalNet = entries.reduce((s, e) => s + e.netPay, 0);
  const totalDeductions = entries.reduce((s, e) => s + e.totalDeductions, 0);

  const entryColumns = [
    { title: 'Employee', dataIndex: 'employeeName', key: 'employeeName' },
    { title: 'Emp No.', dataIndex: 'employeeNumber', key: 'employeeNumber', width: 100 },
    { title: 'Gross', key: 'gross', render: (_: any, r: PayrollEntry) => fmt(r.grossPay), align: 'right' as const },
    { title: 'Deductions', key: 'ded', render: (_: any, r: PayrollEntry) => fmt(r.totalDeductions), align: 'right' as const },
    { title: 'Net Pay', key: 'net', render: (_: any, r: PayrollEntry) => <Text strong>{fmt(r.netPay)}</Text>, align: 'right' as const },
  ];

  const stepContent = [
    // Step 0 — Create period
    <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
      <Form.Item name="periodCode" label="Period Code" rules={[{ required: true }]}>
        <Input placeholder="e.g. JAN-2025" />
      </Form.Item>
      <Form.Item name="periodName" label="Period Name" rules={[{ required: true }]}>
        <Input placeholder="e.g. January 2025" />
      </Form.Item>
      <Form.Item name="dateRange" label="Period Dates" rules={[{ required: true }]}>
        <DatePicker.RangePicker style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="payDate" label="Pay Date">
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="paymentFrequency" label="Frequency" rules={[{ required: true }]}>
        <Select>
          <Option value="MONTHLY">Monthly</Option>
          <Option value="BI_WEEKLY">Bi-Weekly</Option>
          <Option value="WEEKLY">Weekly</Option>
        </Select>
      </Form.Item>
    </Form>,

    // Step 1 — Generate
    <div>
      <p>Click <strong>Generate</strong> to create payroll entries for all active employees based on their salary structures.</p>
      {activePeriod && <Alert type="info" message={`Period: ${activePeriod.periodName}`} style={{ maxWidth: 480, marginBottom: 16 }} />}
    </div>,

    // Step 2 — Review
    <div>
      <div className="flex gap-8 mb-4">
        <Text>Employees: <strong>{entries.length}</strong></Text>
        <Text>Gross: <strong>TZS {fmt(totalGross)}</strong></Text>
        <Text>Deductions: <strong>TZS {fmt(totalDeductions)}</strong></Text>
        <Text>Net Pay: <strong>TZS {fmt(totalNet)}</strong></Text>
      </div>
      <Table dataSource={entries} columns={entryColumns} rowKey="id" size="small" pagination={{ pageSize: 10 }} />
    </div>,

    // Step 3 — Process
    <div>
      <Alert
        type="warning"
        message="Processing will lock entries for editing. Payroll will move to Pending Approval."
        style={{ maxWidth: 480, marginBottom: 16 }}
      />
      {activePeriod && <PayrollStatusBadge status={activePeriod.status} />}
    </div>,

    // Step 4 — Approve & Post
    <div>
      <Alert
        type="success"
        message="Approving will post GL entries to the accounting system. This action cannot be undone."
        style={{ maxWidth: 480, marginBottom: 16 }}
      />
      <div className="flex gap-8 mb-4">
        <Text>Total Gross: <strong>TZS {fmt(totalGross)}</strong></Text>
        <Text>Total Net: <strong>TZS {fmt(totalNet)}</strong></Text>
      </div>
    </div>,
  ];

  const stepActions = [
    <Button type="primary" onClick={handleCreatePeriod} loading={isLoading}>Create Period</Button>,
    <Button type="primary" onClick={handleGenerate} loading={isLoading}>Generate Entries</Button>,
    <Button type="primary" onClick={handleReviewNext}>Proceed to Process</Button>,
    <Button type="primary" onClick={handleProcess} loading={isLoading}>Process Payroll</Button>,
    <Button type="primary" onClick={handleApprove} loading={isLoading}>Approve &amp; Post GL</Button>,
  ];

  return (
    <div className="p-6">
      <Title level={3}>New Payroll Run</Title>
      <Steps current={currentStep} items={STEP_LABELS.map((t) => ({ title: t }))} style={{ marginBottom: 32 }} />
      <Card style={{ marginBottom: 24 }}>{stepContent[currentStep]}</Card>
      <div className="flex gap-4">
        {currentStep > 0 && <Button onClick={() => setCurrentStep((s) => s - 1)}>Back</Button>}
        {stepActions[currentStep]}
        <Button onClick={() => navigate('/hr/payroll')}>Cancel</Button>
      </div>
    </div>
  );
};

export default PayrollRunWizardView;
